/**
 * Legacy custom NS component identity.
 *
 * Instances may still appear in old serialized worker state, so the class and
 * parameter bag remain readable. Applying boundary conditions is deliberately
 * impossible: browser-authored logic, fixed deltas, and heat rates are not a
 * trusted component implementation and must never degrade to passthrough.
 */
import type { NSComponent, Junction, ComponentUpdate } from '../types';
import {
  customComponentExecutionBlock,
  type CustomComponentExecutionBlock,
} from '../../../lib/sim/customComponents/customComponentPackage';

export class UnsupportedCustomComponentExecutionError extends Error {
  readonly code = 'NS_CUSTOM_COMPONENT_NON_EXECUTABLE';
  readonly componentId: string;
  readonly field: CustomComponentExecutionBlock['field'];
  readonly diagnostic: CustomComponentExecutionBlock;

  constructor(
    componentId: string,
    label: string,
    packageValue: unknown,
  ) {
    const diagnostic = customComponentExecutionBlock(componentId, label, packageValue);
    super(diagnostic.message);
    this.name = 'UnsupportedCustomComponentExecutionError';
    this.componentId = componentId;
    this.field = diagnostic.field;
    this.diagnostic = diagnostic;
  }
}
export class NSCustom implements NSComponent {
  id: string;
  type = 'custom';
  params: Record<string, any>;

  constructor(id: string, params: Record<string, any>) {
    this.id = id;
    // Preserve every legacy byte/value for worker and import round-trips. No
    // value in this bag is interpreted as physics by this class.
    this.params = params;
  }

  applyBoundaryConditions(
    _inlet: Junction,
    _outlet: Junction,
    _dt: number,
  ): ComponentUpdate {
    throw new UnsupportedCustomComponentExecutionError(
      this.id,
      typeof this.params.label === 'string' ? this.params.label : this.id,
      this.params.customComponentPackage,
    );
  }
}
